import { useContext, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { AutopilotCard } from '@/components/Common/autopilot-card';
import { EventLogTable } from '@/components/Common/event-log-table';
import { EventTimeline } from '@/components/Common/event-timeline';
import { EnergyUseIntensityCard } from '@/components/Common/energy-use-intensity-card';
import { OperationCard } from '@/components/Common/operation-card';
import { IndoorEnvironmentCard } from '@/components/overview/indoor-environment-card';
import { OccupancyCard } from '@/components/overview/occupancy-card';
import PowerConsumptionCard from '@/components/overview/power-consumption-card';
import EnergyConsumptionCard from '@/components/overview/energy-consumption-card';
import { SolarCard } from '@/components/Common/solar-card';
import EnergyPowerChart from '@/components/Common/energy-power-chart';
import { useAutopilotHook } from '@/api/hooks/useAutopilot';
import { useEventLog } from '@/api/hooks/useEventLog';
import { useDevices } from '@/api/hooks/useDevices';
import { useZones } from '@/api/hooks/useZones';
import { useCamera } from '@/api/hooks/useCamera';
import { useSolar } from '@/api/hooks/useSolar';
import { useIaq } from '@/api/hooks/useIaq';
import { useSitesWithEUI } from '@/api/hooks/useSites';
import { ZoneContext } from '@/auth/zoneProvider';
import { supabase } from '@/api/supabase';
import { AutoPilotStatus, Mode, Status } from '@/types';
import { Power, Sun, Waves, PowerOff, UsersRound, ClipboardList, BarChart3 } from 'lucide-react';
import BUILDING_CONFIG from '@/config/building';
import dayjs from 'dayjs';
import { IconTitle } from '@/components/ui/icon-title';

interface AutopilotData {
  id?: string;
  user?: string;
  savedPower?: number;
  consumption?: number;
  status?: string;
  lastUpdated?: string;
  currentGear?: {
    id: number;
    gear: string;
    config: {
      pmv_offset: number;
      description: string;
    };
  };
  startDate?: string;
  currentMonth?: {
    consumption: number;
    savings: number;
    savingsPercent: number;
  };
  lastMonth?: {
    consumption: number;
    savings: number;
    savingsPercent: number;
  };
}

// Get event types from building config
const eventTypes = BUILDING_CONFIG.eventTypes.map(eventType => {
  // Add icon components based on iconType
  let icon = null;
  switch (eventType.iconType) {
    case 'power':
      icon = <Power className="h-2 w-2 text-white" />;
      break;
    case 'sun':
      icon = <Sun className="h-2 w-2 text-white" />;
      break;
    case 'waves':
      icon = <Waves className="h-2 w-2 text-white" />;
      break;
    case 'powerOff':
      icon = <PowerOff className="h-2 w-2 text-white" />;
      break;
    case 'users':
      icon = <UsersRound className="h-2 w-2 text-white" />;
      break;
  }
  return { ...eventType, icon };
});

interface StageLogData {
  stage: string;
  startedAt: string;
  endedAt: string | null;
  working: number;
  areaTemp: number;
  setTemp: number;
  returnTemp: number;
  occupancy: number;
  solar: number;
  duration: number;
  shadow?: boolean;
}

interface EventLogData {
  time: string;
  event: string;
  name: string;
  working: number;
  areaTemp: number;
  setTemp: number;
  returnTemp: number;
  occupancy: number;
  solar: number;
  shadow?: boolean;
  stages?: StageLogData[];
  isExpanded?: boolean;
}

interface Profile {
  id?: number;
  name?: string;
  automation_profiles?: {
    automation: {
      id: number;
      name: string;
    };
    start_time: string;
    end_time: string;
  }[];
}

export function OverviewPage() {
  const zoneContext = useContext(ZoneContext);
  const [autopilotData, setAutopilotData] = useState<AutopilotData>();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [eventLogData, setEventLogData] = useState<EventLogData[]>([]);
  const [profile, setProfile] = useState<Profile>({});

  // State for new cards
  const [operationalData, setOperationalData] = useState<{
    averageWorking?: number;
    avgToday?: number;
    units: Array<{
      status: Status;
      type: Mode;
      count: number;
    }>;
  }>({
    averageWorking: 0,
    avgToday: 0,
    units: [
      { status: 'on', type: 'cool', count: 0 },
      { status: 'off', type: 'fan', count: 0 },
      { status: 'alarm', type: 'dry', count: 0 }
    ]
  });

  // State for power and energy consumption
  const [consumptionData, setConsumptionData] = useState<{
    powerUsage: number;
    avgTodayPower: number;
    todayEnergy: number;
  }>({
    powerUsage: 0,
    avgTodayPower: 0,
    todayEnergy: 0
  });

  // State for energy resource mapping
  const [energyData, setEnergyData] = useState<{
    solarPower: number | null;
    gridPower: number | null;
    acPower: number | null;
    otherPower: number | null;
    totalPower: number | null;
    solarTodayEnergy: number | null;
    gridTodayEnergy: number | null;
    acTodayEnergy: number | null;
    otherTodayEnergy: number | null;
    totalTodayEnergy: number | null;
  }>({
    solarPower: null,
    gridPower: null,
    acPower: null,
    otherPower: null,
    totalPower: null,
    solarTodayEnergy: null,
    gridTodayEnergy: null,
    acTodayEnergy: null,
    otherTodayEnergy: null,
    totalTodayEnergy: null
  });

  const [tempData, setTempData] = useState<{
    temperature: {
      current: number;
      average: number;
    };
    humidity: {
      current: number;
      average: number;
    };
    sensorStatus: {
      tempHumidity: {
        online: number;
        total: number;
      };
      iaq: {
        online: number;
        total: number;
      };
    };
  }>({
    temperature: { current: 0, average: 0 },
    humidity: { current: 0, average: 0 },
    sensorStatus: {
      tempHumidity: { online: 0, total: 0 },
      iaq: { online: 0, total: 0 }
    }
  });

  const [cameraData, setCameraData] = useState<{
    current: number;
    peopleIn: number;
    peopleOut: number;
    isOnline: boolean;
  }>({
    current: 0,
    peopleIn: 0,
    peopleOut: 0,
    isOnline: false
  });

  const [solarData, setSolarData] = useState<{
    ratio: number | null;
    solarSurplus: number | null;
    irradiance: number | null;
  }>({
    ratio: null,
    solarSurplus: null,
    irradiance: null
  });

  // Data fetching hooks
  const { data: autopilotsData, isLoading: autopilotLoading, refetch: autoPilotRefetch } = useAutopilotHook({ level: 'building', expand: 'total_energy' });
  const { data: eventLogApiData, isLoading: eventLogLoading, refetch: eventLogRefetch } = useEventLog({
    date: selectedDate.format('YYYY-MM-DD'),
    expand: 'stages'
  });
  const { data: systemData, refetch: systemRefetch } = useDevices({ expand: 'latest_data,devices' });
  const { data: zoneData, refetch: zoneRefetch } = useZones({ expand: 'latest_data' }, zoneContext?.id || '');
  const { data: cameraApiData, refetch: cameraRefetch } = useCamera({ expand: 'latest_data' });
  const { data: solarApiData, refetch: solarRefetch } = useSolar({ expand: 'latest_data' });
  const { data: iaqApiData, refetch: iaqRefetch } = useIaq({ expand: 'latest_data,devices' });
  const { data: sitesData, refetch: sitesRefetch } = useSitesWithEUI();

  // Initialize autopilot data from API response
  useEffect(() => {
    if (!autopilotsData?.[0]) return;

    const autopilot = autopilotsData[0];
    setAutopilotData(prev => ({
      ...prev,
      id: String(autopilot.id),
      user: (autopilot as any).updated_by?.username,
      savedPower: (autopilot as any).total_energy?.total_energy_saving?.value,
      consumption: (autopilot as any).total_energy?.total_energy_consumption?.value,
      status: autopilot.autopilot_status,
      lastUpdated: autopilot.updated_at,
      currentGear: autopilot.current_gear,
      startDate: (autopilot as any).start_date || (autopilot as any).created_at,
      currentMonth: {
        consumption: (autopilot as any).current_month?.consumption || 0,
        savings: (autopilot as any).current_month?.savings || 0,
        savingsPercent: (autopilot as any).current_month?.savings_percent || 0
      },
      lastMonth: {
        consumption: (autopilot as any).last_month?.consumption || 0,
        savings: (autopilot as any).last_month?.savings || 0,
        savingsPercent: (autopilot as any).last_month?.savings_percent || 0
      }
    }));
  }, [autopilotsData]);

  // Set up real-time subscription for autopilot changes
  useEffect(() => {
    const autoPilotSubscription = supabase.channel('overview_autopilot_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'autopilot',
        },
        async (payload) => {
          if (!payload.new) return;

          // Type assertion to handle payload.new properties
          const { device_id, autopilot_status, updated_at, updated_by_id } = payload.new as any;

          // Get username from auth_user table using updated_by_id
          let username = undefined;
          if (updated_by_id) {
            const { data: userData, error } = await supabase
              .from('auth_user')
              .select('username')
              .eq('id', updated_by_id)
              .single();

            if (!error && userData) {
              username = userData.username;
            }
          }

          if (device_id === 'alto-cero-dev') {
            setAutopilotData(prev => ({
              ...prev,
              status: autopilot_status,
              lastUpdated: updated_at,
              user: username,
            }));
          }
        }
      )
      .subscribe();

    // Set up subscription for energy data changes
    const energyDataSubscription = supabase.channel('overview_energy_data_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'latest_data',
        },
        (payload) => {
          if (!payload.new) return;

          // Type assertion to handle payload.new properties
          const { device_id, datapoint, value } = payload.new as any;
          if (!device_id || !datapoint) return;

          if (device_id === 'alto-cero-dev' && ['total_energy_saving', 'total_energy_consumption'].includes(datapoint)) {
            setAutopilotData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                [datapoint === 'total_energy_saving' ? 'savedPower' : 'consumption']: value,
              };
            });
          }

          // Update average working percentage for operation card
          if (device_id === 'ac_subsystem' && datapoint === 'avg_working_percentage') {
            setOperationalData(prev => ({
              ...prev,
              averageWorking: value || 0 // Keep decimal places
            }));
          }

          // Update power and energy consumption
          if (device_id === 'ac_subsystem' && ['power', 'today_avg_power', 'today_energy'].includes(datapoint)) {
            setConsumptionData(prev => ({
              ...prev,
              [datapoint === 'power' ? 'powerUsage' : 
               datapoint === 'today_avg_power' ? 'avgTodayPower' : 
               'todayEnergy']: value || 0
            }));
          }

          // Update zone temperature and humidity data
          if (device_id === zoneContext?.id && ['avg_room_temperature', 'avg_relative_humidity', 'today_avg_room_temperature', 'today_avg_relative_humidity'].includes(datapoint)) {
            setTempData(prev => ({
              ...prev,
              temperature: {
                ...prev.temperature,
                current: datapoint === 'avg_room_temperature' ? value : prev.temperature.current,
                average: datapoint === 'today_avg_room_temperature' ? value : prev.temperature.average
              },
              humidity: {
                ...prev.humidity,
                current: datapoint === 'avg_relative_humidity' ? value : prev.humidity.current,
                average: datapoint === 'today_avg_relative_humidity' ? value : prev.humidity.average
              }
            }));
          }

          // Update energy data for solar subsystem
          if (device_id === 'solar_subsystem' && ['power', 'grid_power', 'today_energy', 'today_grid_energy'].includes(datapoint)) {
            setEnergyData(prev => {
              const updatedData = {
                ...prev,
                [datapoint === 'power' ? 'solarPower' : 
                 datapoint === 'grid_power' ? 'gridPower' : 
                 datapoint === 'today_energy' ? 'solarTodayEnergy' : 
                 'gridTodayEnergy']: value ?? null
              };
              
              // Get updated values
              const newSolarPower = datapoint === 'power' ? value : prev.solarPower;
              const newGridPower = datapoint === 'grid_power' ? value : prev.gridPower;
              const newSolarEnergy = datapoint === 'today_energy' ? value : prev.solarTodayEnergy;
              const newGridEnergy = datapoint === 'today_grid_energy' ? value : prev.gridTodayEnergy;
              
              // Recalculate totals
              updatedData.totalPower = (newGridPower !== null && newSolarPower !== null) ? newGridPower + newSolarPower : null;
              updatedData.totalTodayEnergy = (newGridEnergy !== null && newSolarEnergy !== null) ? newGridEnergy + newSolarEnergy : null;
              
              // Recalculate Other = Solar + Grid - AC
              updatedData.otherPower = (newSolarPower !== null && newGridPower !== null && prev.acPower !== null) 
                ? newSolarPower + newGridPower - prev.acPower : null;
              updatedData.otherTodayEnergy = (newSolarEnergy !== null && newGridEnergy !== null && prev.acTodayEnergy !== null) 
                ? newSolarEnergy + newGridEnergy - prev.acTodayEnergy : null;
              
              return updatedData;
            });
          }

          // Update energy data for AC subsystem (power and energy)
          if (device_id === 'ac_subsystem' && ['power', 'today_energy'].includes(datapoint)) {
            setEnergyData(prev => {
              const updatedData = {
                ...prev,
                [datapoint === 'power' ? 'acPower' : 'acTodayEnergy']: value ?? null
              };
              
              // Get updated AC values
              const newAcPower = datapoint === 'power' ? value : prev.acPower;
              const newAcEnergy = datapoint === 'today_energy' ? value : prev.acTodayEnergy;
              
              // Recalculate Other = Solar + Grid - AC
              updatedData.otherPower = (prev.solarPower !== null && prev.gridPower !== null && newAcPower !== null) 
                ? prev.solarPower + prev.gridPower - newAcPower : null;
              updatedData.otherTodayEnergy = (prev.solarTodayEnergy !== null && prev.gridTodayEnergy !== null && newAcEnergy !== null) 
                ? prev.solarTodayEnergy + prev.gridTodayEnergy - newAcEnergy : null;
              
              return updatedData;
            });
          }
        }
      )
      .subscribe();

    // Clean up subscriptions on component unmount
    return () => {
      autoPilotSubscription.unsubscribe();
      energyDataSubscription.unsubscribe();
    };
  }, []);

  // Process event log data
  useEffect(() => {
    if (!eventLogApiData) return;

    try {
      // Check if data exists and is an array
      const eventData = eventLogApiData as any;
      if (Array.isArray(eventData?.data)) {
        setEventLogData(eventData.data.map((event: any) => {
          // Process stages if they exist
          const stages = event.stages?.map((stage: any) => ({
            stage: stage.stage,
            startedAt: stage.started_at,
            endedAt: stage.ended_at,
            working: stage.metadata?.avg_working_percentage || 0,
            areaTemp: stage.metadata?.avg_room_temperature || 0,
            setTemp: stage.metadata?.avg_temperature_setpoint || 0,
            returnTemp: stage.metadata?.avg_return_temperature || 0,
            occupancy: stage.metadata?.occupancy_current || 0,
            solar: stage.metadata?.solar_surplus_rate || 0,
            duration: stage.metadata?.stage_duration_minutes || 0,
            shadow: stage.shadow || false
          })) || [];

          // Find the stage with the latest end time (or ongoing stage)
          let latestEndTime = null;
          let isOngoing = false;
          
          stages.forEach(stage => {
            if (!stage.endedAt) {
              // If any stage has no end time, the event is ongoing
              isOngoing = true;
            } else if (!latestEndTime || new Date(stage.endedAt) > new Date(latestEndTime)) {
              latestEndTime = stage.endedAt;
            }
          });
          
          return {
            time: latestEndTime ? dayjs(latestEndTime).format('HH:mm') : isOngoing ? 'Now' : dayjs(event.started_at).format('HH:mm'),
            event: event.blueprint_name || '',
            name: event.automation_name || '',
            working: event.metadata?.avg_working_percentage || 0,
            areaTemp: event.metadata?.avg_room_temperature || 0,
            setTemp: event.metadata?.avg_temperature_setpoint || 0,
            returnTemp: event.metadata?.avg_return_temperature || 0,
            occupancy: event.metadata?.occupancy_current || 0,
            solar: event.metadata?.solar_surplus_rate || 0,
            shadow: event.shadow || false,
            stages: stages,
            isExpanded: false,
            isOngoing: isOngoing
          };
        }));
      } else {
        // If data is not an array, set empty array
        setEventLogData([]);
      }

      // Safely set profile data if available
      if (eventLogApiData && typeof eventLogApiData === 'object') {
        const metadata = eventLogApiData.metadata as any;
        if (metadata) {
          setProfile({
            ...(metadata.profile || {}),
            name: metadata.profile_name || '',
          });
        }
      }
    } catch (error) {
      console.error('Error processing event log data:', error);
      // Set empty data on error
      setEventLogData([]);
      setProfile({});
    }
  }, [eventLogApiData]);

  // Process system data for Operation card
  useEffect(() => {
    if (!systemData) {
      return;
    }

    // Get average working percentage from API (same as Device and Autopilot pages)
    const avgWorking = systemData.latest_data?.avg_working_percentage?.value || 0;

    // Get power and energy consumption from API
    setConsumptionData({
      powerUsage: systemData.latest_data?.power?.value || 0,
      avgTodayPower: systemData.latest_data?.today_avg_power?.value || 0,
      todayEnergy: systemData.latest_data?.today_energy?.value || 0
    });

    // Get working devices for temperature calculation
    const workingDevices = systemData.devices?.filter(device =>
      device.latest_data?.status_read?.value === 1 ||
      Boolean(device.latest_data?.status_read?.value)
    ) || [];

    // Count units by mode and status
    const alarmUnits = systemData.devices?.filter(device => {
      return device.latest_data?.alarm?.value === 1 ||
             Boolean(device.latest_data?.alarm?.value);
    }).length || 0;

    const onUnits = systemData.devices?.filter(device => {
      return (device.latest_data?.status_read?.value === 1 ||
              Boolean(device.latest_data?.status_read?.value)) &&
              !(device.latest_data?.alarm?.value === 1 ||
                Boolean(device.latest_data?.alarm?.value));
    }).length || 0;

    const offUnits = systemData.devices?.filter(device => {
      const isOff = !(device.latest_data?.status_read?.value === 1 ||
                      Boolean(device.latest_data?.status_read?.value));
      const isOnline = device.latest_data?.online_status?.value === 1 || 
                       Boolean(device.latest_data?.online_status?.value);
      return isOff && isOnline; // Only count devices as 'off' if they are online but turned off
    }).length || 0;

    // Count units by mode (only for ON devices, matching AHU page logic)
    const coolUnits = systemData.devices?.filter(device => {
      const isOn = device.latest_data?.status_read?.value === 1 || Boolean(device.latest_data?.status_read?.value);
      const mode = device.latest_data?.mode_read?.definition?.toLowerCase();
      return isOn && mode === 'cool';
    }).length || 0;

    const fanUnits = systemData.devices?.filter(device => {
      const isOn = device.latest_data?.status_read?.value === 1 || Boolean(device.latest_data?.status_read?.value);
      const mode = device.latest_data?.mode_read?.definition?.toLowerCase();
      return isOn && mode === 'fan';
    }).length || 0;

    const dryUnits = systemData.devices?.filter(device => {
      const isOn = device.latest_data?.status_read?.value === 1 || Boolean(device.latest_data?.status_read?.value);
      const mode = device.latest_data?.mode_read?.definition?.toLowerCase();
      return isOn && mode === 'dry';
    }).length || 0;

    // Calculate average temperature setpoint
    const avgTempSetpoint = workingDevices.length > 0
      ? workingDevices.reduce((sum, device) => sum + (device.latest_data?.temperature_setpoint_read?.value || 0), 0) / workingDevices.length
      : 0;

    setOperationalData({
      averageWorking: avgWorking, // Keep decimal places
      avgToday: Math.round(avgTempSetpoint * 10) / 10, // Round to 1 decimal place
      units: [
        { status: 'alarm', count: alarmUnits },
        { status: 'on', count: onUnits },
        { status: 'off', count: offUnits },
        { type: 'cool', count: coolUnits },
        { type: 'fan', count: fanUnits },
        { type: 'dry', count: dryUnits }
      ]
    });
  }, [systemData]);

  // Process zone data for Environment card
  useEffect(() => {
    // Get zone data if it's a single zone
    const zoneDataObj = Array.isArray(zoneData) ? zoneData[0] : zoneData;

    // Use type assertion to handle zone data
    const zoneLatestData = zoneDataObj ? (zoneDataObj as any).latest_data : null;

    if (!zoneLatestData || !systemData || !iaqApiData) {
      return;
    }

    // Calculate sensor status from IAQ subsystem
    const iaqDevices = iaqApiData?.data?.devices || [];
    
    // Separate temp/humidity sensors (th_X) from IAQ sensors (iaq_X)
    const tempHumidityDevices = iaqDevices.filter(device => device.device_id.startsWith('th_'));
    const iaqSensorDevices = iaqDevices.filter(device => device.device_id.startsWith('iaq_'));
    
    // Count temp/humidity sensors
    const totalTempHumiditySensors = tempHumidityDevices.length;
    const onlineTempHumiditySensors = tempHumidityDevices.filter(device => 
      device.latest_data?.online_status?.value === 1 || 
      Boolean(device.latest_data?.online_status?.value)
    ).length;

    // Count IAQ sensors
    const totalIAQSensors = iaqSensorDevices.length;
    const onlineIAQSensors = iaqSensorDevices.filter(device => 
      device.latest_data?.online_status?.value === 1 || 
      Boolean(device.latest_data?.online_status?.value)
    ).length;

    setTempData({
      temperature: {
        current: zoneLatestData.avg_room_temperature?.value || 0,
        average: zoneLatestData.today_avg_room_temperature?.value || zoneLatestData.avg_room_temperature_today?.value || 0
      },
      humidity: {
        current: zoneLatestData.avg_relative_humidity?.value || 0,
        average: zoneLatestData.today_avg_relative_humidity?.value || zoneLatestData.avg_relative_humidity_today?.value || 0
      },
      sensorStatus: {
        tempHumidity: {
          online: onlineTempHumiditySensors,
          total: totalTempHumiditySensors
        },
        iaq: {
          online: onlineIAQSensors,
          total: totalIAQSensors
        }
      }
    });
  }, [zoneData, systemData, iaqApiData]);

  // Process camera data for Occupancy card
  useEffect(() => {
    if (!cameraApiData?.latest_data) {
      return;
    }

    setCameraData({
      current: cameraApiData.latest_data?.occupancy_current?.value || 0,
      peopleIn: cameraApiData.latest_data?.in_count_daily?.value ||
                cameraApiData.latest_data?.today_in_count?.value || 0,
      peopleOut: cameraApiData.latest_data?.out_count_daily?.value ||
                 cameraApiData.latest_data?.today_out_count?.value || 0,
      isOnline: cameraApiData.latest_data?.online_status?.value === 1 ||
                Boolean(cameraApiData.latest_data?.online_status?.value) || false
    });
  }, [cameraApiData]);

  // Process energy data from all subsystems
  useEffect(() => {
    // Extract solar data - return null if data is not available
    const solarPower = solarApiData?.latest_data?.power?.value ?? null;
    const gridPower = solarApiData?.latest_data?.grid_power?.value ?? null;
    const solarTodayEnergy = solarApiData?.latest_data?.today_energy?.value ?? null;
    const gridTodayEnergy = solarApiData?.latest_data?.today_grid_energy?.value ?? null;

    // Extract AC data (already available in systemData)
    const acPower = systemData?.latest_data?.power?.value ?? null;
    const acTodayEnergy = systemData?.latest_data?.today_energy?.value ?? null;

    // Calculate Other = Solar + Grid - AC (only if all values are available)
    const otherPower = (solarPower !== null && gridPower !== null && acPower !== null) 
      ? solarPower + gridPower - acPower : null;
    const otherTodayEnergy = (solarTodayEnergy !== null && gridTodayEnergy !== null && acTodayEnergy !== null) 
      ? solarTodayEnergy + gridTodayEnergy - acTodayEnergy : null;

    // Calculate totals only if both values are available
    const totalPower = (gridPower !== null && solarPower !== null) ? gridPower + solarPower : null;
    const totalTodayEnergy = (gridTodayEnergy !== null && solarTodayEnergy !== null) ? gridTodayEnergy + solarTodayEnergy : null;

    setEnergyData({
      solarPower,
      gridPower,
      acPower,
      otherPower,
      totalPower,
      solarTodayEnergy,
      gridTodayEnergy,
      acTodayEnergy,
      otherTodayEnergy,
      totalTodayEnergy
    });
  }, [solarApiData, systemData]);

  // Process solar data for Solar card
  useEffect(() => {
    if (!solarApiData?.latest_data) {
      return;
    }

    setSolarData({
      ratio: solarApiData.latest_data?.solar_surplus_rate?.value ?? null,
      solarSurplus: solarApiData.latest_data?.solar_surplus?.value ?? null,
      irradiance: solarApiData.latest_data?.irradiance?.value ?? null
    });
  }, [solarApiData]);

  // Initial data fetch
  useEffect(() => {
    autoPilotRefetch();
    eventLogRefetch();
    systemRefetch();
    zoneRefetch();
    cameraRefetch();
    solarRefetch();
    iaqRefetch();
    sitesRefetch();
  }, [autoPilotRefetch, eventLogRefetch, systemRefetch, zoneRefetch, cameraRefetch, solarRefetch, iaqRefetch, sitesRefetch]);

  // Set up polling as a fallback for real-time updates
  useEffect(() => {
    // Poll every 5 minutes (300000 ms) as a fallback
    const pollingInterval = setInterval(() => {
      console.log('Fallback polling: refreshing data');
      autoPilotRefetch();
      eventLogRefetch();
      systemRefetch();
      zoneRefetch();
      cameraRefetch();
      solarRefetch();
      iaqRefetch();
      sitesRefetch();
    }, 300000);

    return () => clearInterval(pollingInterval);
  }, [autoPilotRefetch, eventLogRefetch, systemRefetch, zoneRefetch, cameraRefetch, solarRefetch, iaqRefetch, sitesRefetch]);

  return (
    <div className="p-4 space-y-4 font-inter h-full overflow-auto" style={{ fontFamily: 'Inter, sans-serif !important' }}>
      <div className="grid grid-cols-12 gap-4">
        {/* Left Column (3/12) */}
        <div className="col-span-3">
          <div className="h-full flex flex-col gap-3">
            {/* Energy Use Intensity Card */}
            <EnergyUseIntensityCard
              value={sitesData?.[0]?.eui}
              gfa={sitesData?.[0]?.metadata?.area || BUILDING_CONFIG.gfa}
              hideValue={!sitesData?.[0]?.eui}
              date="2025-01-01"
              yearToDateEnergy={sitesData?.[0]?.metadata?.year_to_date_energy}
            />

            {/* Operation Card */}
            <OperationCard
              units={operationalData.units}
              info={{
                averageWorking: operationalData.averageWorking || 0,
                avgToday: operationalData.avgToday || 0,
              }}
              variant="overview"
            />

            {/* Indoor Environment Card */}
            <IndoorEnvironmentCard
              temperature={tempData.temperature}
              humidity={tempData.humidity}
              sensorStatus={tempData.sensorStatus}
            />

            {/* Solar Card */}
            <SolarCard
              ratio={solarData.ratio}
              solarSurplus={solarData.solarSurplus}
              irradiance={solarData.irradiance}
            />

            {/* Occupancy Card */}
            <OccupancyCard
              currentOccupancy={cameraData.current}
              peopleIn={cameraData.peopleIn}
              peopleOut={cameraData.peopleOut}
              isOnline={cameraData.isOnline}
              deviceStatus={{ online: 5, total: 5 }} // TODO: Replace with real CCTV device status data
            />
          </div>
        </div>

        {/* Middle Column (6/12) */}
        <div className="col-span-6 flex flex-col gap-3">
          {/* Event Timeline Container */}
          <div className="bg-white rounded-lg border border-light-grey p-4">
            <EventTimeline
              eventTypes={eventTypes}
              profileName={profile?.name}
              showEventTypes={true}
              eventLogData={(eventLogApiData as any)?.data || []}
              isLoading={eventLogLoading}
              selectedDate={selectedDate}
            />
          </div>

          {/* Energy Section Container */}
          <div className="bg-white rounded-lg border border-light-grey p-3 flex flex-col gap-1">
            {/* Title */}
            <div className="mb-1">
              <IconTitle icon={BarChart3} title="Power Consumption & Generation" />
            </div>

            {/* Add Energy Power Chart */}
            <div className="h-[265px]">
              <EnergyPowerChart selectedDate={selectedDate} />
            </div>

            {/* Power and Energy Cards - Grid Layout */}
            <div className="grid grid-cols-12 gap-4">
              {/* Power Consumption - 6 cols */}
              <div className="col-span-6">
                <PowerConsumptionCard
                  totalConsumption={energyData.totalPower}
                  acConsumption={energyData.acPower}
                  otherConsumption={energyData.otherPower}
                  solarProduced={energyData.solarPower}
                  gridImport={energyData.gridPower}
                  avgTodayPower={consumptionData.avgTodayPower}
                />
              </div>

              {/* Energy Consumption - 6 cols */}
              <div className="col-span-6">
                <EnergyConsumptionCard
                  acConsumption={energyData.acTodayEnergy}
                  otherConsumption={energyData.otherTodayEnergy}
                  solarProduced={energyData.solarTodayEnergy}
                  gridImport={energyData.gridTodayEnergy}
                  todayEnergy={energyData.totalTodayEnergy}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (3/12) */}
        <div className="col-span-3">
          <div className="flex flex-col gap-4">
            {autopilotLoading || !autopilotData ? (
              <div className="p-4 bg-white rounded-lg border space-y-3">
                <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-4 w-2/3">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
                </div>
                <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-full">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
                </div>
                <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-3 w-1/2">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
                </div>
              </div>
            ) : (
              <AutopilotCard
                id={autopilotData?.id || ''}
                user={autopilotData?.user}
                savedPower={autopilotData?.savedPower}
                status={autopilotData?.status as AutoPilotStatus}
                lastUpdated={autopilotData?.lastUpdated}
                isMonthlyReport={true}
                startDate={autopilotData?.startDate ? dayjs(autopilotData.startDate).format('MMMM YYYY') : 'May 2025'}
                showGear={true}
                currentGear={autopilotData?.currentGear}
                currentMonth={autopilotData?.currentMonth || {
                  consumption: 0,
                  savings: 0,
                  savingsPercent: 0
                }}
                lastMonth={autopilotData?.lastMonth || {
                  consumption: 0,
                  savings: 0,
                  savingsPercent: 0
                }}
              />
            )}

            {/* Event Log Section */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center mb-1 flex-shrink-0">
                <IconTitle icon={ClipboardList} title="Event Log" />
              </div>

              {/* Event Log Table */}
              <div className="h-[600px] flex flex-col">
                <EventLogTable
                  eventLogData={eventLogData}
                  eventTypes={eventTypes}
                  isLoading={eventLogLoading}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  onRefetch={eventLogRefetch}
                  showDataColumns={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
